import StoreLocationCard from "./StoreLocationCard";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useLoadScript,
  GoogleMap,
  MarkerF,
  InfoWindow,
  StandaloneSearchBox,
  DistanceMatrixService,
} from "@react-google-maps/api";
import useCountryData from "src/hooks/useCountryData";
import {
  Coordinates,
  ShemsiMapMarker,
  ShemsiUserMapMarker,
  StoreLocation,
  googleMapsLibraries,
} from "src/types/store-locations";
import { useTranslations } from "next-intl";
import Loading from "src/sections/loading/Loading";
import Link from "next/link";
import ThemeLink from "../link/ThemeLink";
import React from "react";
import structuredClone from "@ungap/structured-clone";
import { useMediaQuery } from "usehooks-ts";
import { captureException } from "@sentry/nextjs";

type SearchBox = google.maps.places.SearchBox;

const StoreFinder = () => {
  const t = useTranslations("StoreLocator");
  const countryData = useCountryData();
  const mdBreakpointReached = useMediaQuery("1024");

  const searchBoxRef = useRef<SearchBox>();

  // Initial state for dubai's coordinates, it gets set as the country's map center coordinates when the country data loads
  const [mapCenter, setMapCenter] = useState<Coordinates>({
    lng: 55.272610690480505,
    lat: 25.223944532804772,
  });

  const [selectedLocation, setSelectedLocation] = useState<StoreLocation | null>(null);
  const [searchedLocation, setSearchedLocation] = useState<{
    address: string;
    coordinates: Coordinates;
  } | null>(null);
  const [distanceMatrix, setDistanceMatrix] = useState<google.maps.DistanceMatrixResponse | null>(null);
  const [storeLocations, setStoreLocations] = useState<Coordinates[] | null>(null);
  const [control, setControl] = useState(true);
  const [customerLocation, setCustomerLocation] = useState<Coordinates | null>(null);
  const [searchLocationText, setSearchLocationText] = useState("");
  const [storesData, setStoresData] = useState<StoreLocation[] | undefined>();

  // Get country's store data
  useEffect(() => {
    setStoresData(countryData?.storeLocations);
  }, [countryData?.code]);

  // Get user's location coordinates
  useEffect(() => {
    // Set map center to country's map center
    if (countryData && countryData.storeLocatorMapCenter) {
      setMapCenter(countryData.storeLocatorMapCenter);
    }
    try {
      // Get user's location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setCustomerLocation({ lng: longitude, lat: latitude });
            // setMapCenter({ lat: latitude, lng: longitude });
          },
          (error) => {
            // Don't throw an error if user's location services are disabled
            if (
              error.code === 1 ||
              ["denied Geolocation", "geolocation permissions", "Location Services not available"].some(
                (errorMessage) => error.message.includes(errorMessage)
              )
            ) {
              return; // ignore
            }
            captureException(error.message);
          }
        );
      } else {
        captureException("Geolocation is not supported by this browser.");
      }
    } catch (error: any) {
      captureException(error.message);
    }
  }, [countryData]);

  // Create an array of the stores' coordinates to pass to distance matrix component
  useEffect(() => {
    if (countryData?.storeLocations) {
      const storeLocationsCoordinates = countryData.storeLocations.map(
        (location: StoreLocation) => location.coordinates
      );
      setStoreLocations(storeLocationsCoordinates);
    }
  }, [countryData]);

  // Remove selected location's marker if the search is empty
  useEffect(() => {
    if (searchLocationText.length < 1) {
      setSearchedLocation(null);
      setControl(true);
    }
  }, [searchLocationText]);

  // Handling choosing a location from StandaloneSearchBox component or a marker on the map
  const handleLocationClick = (location: any) => {
    setSelectedLocation(location);
    setMapCenter(location.coordinates);
  };

  const handleInfoWindowClose = () => {
    setSelectedLocation(null);
  };

  const mapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      clickableIcons: true,
      scrollwheel: true,
      draggable: true,
    }),
    []
  );

  // Loading the map
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API as string,
    version: "weekly",
    region: countryData?.code || "EG",
    libraries: googleMapsLibraries,
  });

  useEffect(() => {
    if (loadError) {
      captureException(loadError.message);
    }
  }, [loadError]);

  // Handle event when the search box is loaded
  const onSearchBoxLoad = useCallback((searchBox: SearchBox) => {
    searchBoxRef.current = searchBox;
  }, []);

  // Handle event when the location is changed
  const onPlacesChanged = () => {
    const searchBox = searchBoxRef.current;
    setControl(true);
    if (!searchBox) return;
    // Get the place results from the search box
    const places = searchBox.getPlaces();
    if (!places || places.length === 0) return;

    // Get the lat and lng of the first result
    const lat = places[0].geometry?.location?.lat();
    const lng = places[0].geometry?.location?.lng();
    const address = places[0].adr_address;
    // Set the coordinates state
    if (lat && lng && address) {
      setSearchedLocation({
        address: address,
        coordinates: { lat, lng },
      });
      setMapCenter({ lat, lng });
    }
  };

  // Get distance matrix response to get distance from selected or user's location to stores
  const handleDistanceMatrixResponse = (response: any, status: any) => {
    if (status === "OK") {
      setDistanceMatrix(response);
      setControl(false);
    } else {
      captureException(`Error fetching distance matrix: ${status}`);
    }
  };

  useEffect(() => {
    if (distanceMatrix)
      setStoresData((prevData: StoreLocation[] | undefined) =>
        prevData?.map((store, index) => {
          return {
            ...store,
            distanceMatrixData: distanceMatrix.rows[0].elements[index],
          };
        })
      );
  }, [distanceMatrix]);

  // Countries with no offline stores
  if (!countryData?.storeLocations) {
    return (
      <section className="flex flex-col items-center gap-4 text-center">
        <h2 className="title-2">{t("no_stores_available")}</h2>
        <Link href={"/shop-all"} passHref legacyBehavior>
          <ThemeLink className="w-1/2">{t("shop_now")}</ThemeLink>
        </Link>
      </section>
    );
  }

  // Failed to load map
  if (loadError) {
    return (
      <main>
        <h2 className="title-2">{t("failed_to_load")}</h2>
      </main>
    );
  }
  // Sorting the stores
  let sortedStores: any[] = [];

  if (storesData) {
    sortedStores = structuredClone(storesData);
    sortedStores.sort((a: any, b: any) => {
      return a.distanceMatrixData?.distance?.value - b.distanceMatrixData?.distance?.value;
    });
  }

  return (
    <>
      <div className="space-y-8">
        <div className="flex flex-col items-center space-y-2">
          <h2 className="title-1">{t("find_shemsi_products")}</h2>
          {countryData && (
            <p className="text-lg text-gray-600">
              {t(countryData.storeLocations.length < 11 ? "available_in_n_stores" : "available_in_n_store", {
                numberOfStores: countryData.storeLocations.length,
              })}
            </p>
          )}
        </div>
        <main className="flex flex-col-reverse gap-4 lg:h-[800px] lg:flex-row">
          <section className="flex flex-col gap-4 overflow-auto lg:h-full">
            {/* Location search */}

            {isLoaded && (
              <StandaloneSearchBox onLoad={onSearchBoxLoad} onPlacesChanged={onPlacesChanged}>
                <input
                  type="text"
                  className="input-text"
                  placeholder={t("enter_a_place")}
                  onChange={(e) => setSearchLocationText(e.target.value)}
                />
              </StandaloneSearchBox>
            )}
            {/* Store locations cards*/}
            <div className="flex h-full w-full gap-4 overflow-auto pb-1 lg:flex-col lg:pe-4">
              {sortedStores.map((location: StoreLocation) => (
                <StoreLocationCard
                  key={location.placeId}
                  location={location}
                  selectedLocation={selectedLocation}
                  onClick={() => {
                    handleLocationClick(location);
                  }}
                />
              ))}
            </div>
          </section>
          {/* Map */}
          {!isLoaded ? (
            <Loading text={t("loading_store_locator")} />
          ) : (
            <GoogleMap
              options={mapOptions}
              zoom={mdBreakpointReached ? 10 : 11}
              center={mapCenter}
              mapTypeId={google.maps.MapTypeId.ROADMAP}
              mapContainerClassName="w-full lg:w-3/4 flex h-[400px] lg:h-[800px]"
              onClick={handleInfoWindowClose}
            >
              {/* Distance matrix component that calculates distance between either user's location or searched location and the stores */}
              {(searchedLocation || customerLocation) && storeLocations && control && (
                <DistanceMatrixService
                  options={{
                    origins: [
                      searchedLocation ? searchedLocation.coordinates : customerLocation ? customerLocation : mapCenter,
                    ],
                    destinations: storeLocations,
                    travelMode: google.maps.TravelMode.DRIVING,
                  }}
                  callback={handleDistanceMatrixResponse}
                />
              )}
              {/* Store locations' markers */}
              {countryData.storeLocations.map((location: StoreLocation) => (
                <Fragment key={location.placeId}>
                  <MarkerF
                    position={location.coordinates}
                    icon={ShemsiMapMarker}
                    onClick={() => {
                      handleLocationClick(location);
                    }}
                    animation={google.maps.Animation.DROP}
                  />

                  {selectedLocation && (
                    <InfoWindow
                      position={{
                        lat: selectedLocation.coordinates.lat + 0.000065,
                        lng: selectedLocation.coordinates.lng,
                      }}
                      onCloseClick={handleInfoWindowClose}
                    >
                      <div className="flex flex-col items-center">
                        <span className="font-medium">{t(selectedLocation.storeName)}</span>
                        <a href={selectedLocation.googleMapsLink} target="_blank">
                          <span className="text-xs font-medium text-blue-500">{t("view_on_google_maps")}</span>
                        </a>
                      </div>
                    </InfoWindow>
                  )}
                </Fragment>
              ))}
              {/* User's location if available*/}
              {!searchedLocation && customerLocation && (
                <MarkerF
                  position={customerLocation}
                  icon={ShemsiUserMapMarker}
                  onClick={() => {
                    setMapCenter(customerLocation);
                  }}
                  animation={google.maps.Animation.BOUNCE}
                />
              )}
              {/* Selected option from location search */}
              {searchedLocation && (
                <MarkerF
                  position={searchedLocation.coordinates}
                  icon={ShemsiUserMapMarker}
                  onClick={() => {
                    setMapCenter(searchedLocation.coordinates);
                  }}
                  animation={google.maps.Animation.BOUNCE}
                />
              )}
            </GoogleMap>
          )}
        </main>
      </div>
    </>
  );
};

export default StoreFinder;
